import { useEffect, useState } from 'react';
import { drbfmService } from '../../services/drbfm.service';
import { EvidenceContractError, failureModeToLegacyPayload } from '../../services/evidence.mappers';
import type { FailureMode } from '../../types/drbfm.results';
import type { LegacyConversionNote, ReviewFinding } from '../../types/evidence';
import { apiErrorStatus } from '../../utils/apiErrorMessages';

/**
 * Which failure, so the UI can say the right thing: `retryable` (5xx, transport, a rate
 * limit or a timeout), `permanent` (a 4xx that will not fix itself), `permissionDenied`
 * (401/403 — the viewer, not the data), `contract` (the wire lacks #1195's availability
 * contract — a deployment mismatch, not a failure to retry).
 */
export type EvidenceErrorKind = 'retryable' | 'permanent' | 'permissionDenied' | 'contract';

interface FailureModeEvidence {
  finding: ReviewFinding | null;
  /** In flight. Distinct from "no evidence" — collapsing them states a false result. */
  loading: boolean;
  /** The evidence adapters are switched off for this deployment; render nothing. */
  featureUnavailable: boolean;
  /** What the conversion could NOT carry into evidence — never dropped silently. */
  notes: LegacyConversionNote[];
  /** A load failure stays distinct from "no evidence" all the way to the UI (invariant 4). */
  error: Error | null;
  errorKind: EvidenceErrorKind | null;
  /** No design-change revision to state, so evidence is never requested (invariant 3). */
  revisionContextMissing: boolean;
  /** No creation time to send as `retrieved_at`: the browser clock is not provenance. */
  retrievalTimeMissing: boolean;
}

/** The interceptor rejects with a plain object, so `cause` is rarely a real `Error`. */
const messageOf = (cause: unknown): Error => (
  cause instanceof Error ? cause : new Error((cause as { code?: string })?.code ?? 'conversion failed')
);

const kindOf = (cause: unknown, status: number | undefined): EvidenceErrorKind => {
  if (cause instanceof EvidenceContractError) return 'contract';
  if (status === 401 || status === 403) return 'permissionDenied';
  // A 4xx will not fix itself — except the two that are purely temporal. 429 is reachable
  // here: `services/api.ts` auto-retries idempotent methods only, and this is a POST, so a
  // rate limit always reaches the hook. An unreachable backend has no status at all.
  const temporal = status === 429 || status === 408;
  return status === undefined || status >= 500 || temporal ? 'retryable' : 'permanent';
};

/**
 * The shared evidence contract for one selected failure mode (#966, Story #928).
 *
 * Keyed on what is actually SENT — the converted payload plus the subject revision — so the
 * conversion runs once per distinct input, not per render: each call writes an
 * `evidence.redaction.apply` audit event (`api/routes/evidence.py`). Keying on the id alone
 * would keep showing evidence minted from a pre-edit claim after main's revision composer
 * edits a failure mode in place under the same id (invariant 2).
 */
export const useFailureModeEvidence = (
  failureMode: FailureMode | null,
): FailureModeEvidence => {
  const subjectRevisionId = failureMode?.designChangeId;
  const retrievedAt = failureMode?.createdAt;
  const payloadKey = failureMode ? JSON.stringify(failureModeToLegacyPayload(failureMode)) : undefined;
  const canRequest = Boolean(failureMode && subjectRevisionId && retrievedAt);

  const [finding, setFinding] = useState<ReviewFinding | null>(null);
  const [notes, setNotes] = useState<LegacyConversionNote[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [errorKind, setErrorKind] = useState<EvidenceErrorKind | null>(null);
  const [loading, setLoading] = useState(canRequest);
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  // Everything the effect keys on, so a change in ANY of it resets in the same render.
  const requestKey = [payloadKey, subjectRevisionId, retrievedAt].join('\u0000');
  const [renderedFor, setRenderedFor] = useState(requestKey);

  // Reset DURING render, not in the effect. Effects flush after the commit, so resetting
  // there would commit one frame in which the newly selected failure mode's title sits
  // beside the PREVIOUS one's evidence, generator version and live document link —
  // evidence attributed to the wrong finding (invariant 2). This is React's documented
  // "adjust state when a prop changes" pattern (react.dev, "You Might Not Need an Effect").
  if (renderedFor !== requestKey) {
    setRenderedFor(requestKey);
    setFinding(null);
    setNotes([]);
    setError(null);
    setErrorKind(null);
    setFeatureUnavailable(false);
    setLoading(canRequest);
  }

  useEffect(() => {
    if (!failureMode || !subjectRevisionId || !retrievedAt) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    drbfmService
      .convertFailureModeEvidence(failureMode, subjectRevisionId)
      .then((conversion) => {
        if (cancelled) return;
        setFinding(conversion.finding);
        setNotes(conversion.notes);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        // A 404 here is the feature flag, not a failure: the route dependency
        // (`api/routes/evidence.py`) 404s when ENABLE_REVY_EVIDENCE_ADAPTERS is off,
        // which is the shipped default. Painting an error alert on every panel open in the
        // default configuration would be a regression, so the section stays absent. A
        // route or proxy drift is also a 404 and is absorbed the same way — stated in the
        // PR; #1195's follow-up is a machine-readable code for the flag.
        const status = apiErrorStatus(cause);
        if (status === 404) {
          setFeatureUnavailable(true);
        } else {
          setErrorKind(kindOf(cause, status));
          setError(messageOf(cause));
        }
        setLoading(false);
      });
    return () => { cancelled = true; };
    // Keyed on the serialized payload and the revision, not on `failureMode`: the object
    // gets a new identity on every results refresh, so listing it would re-POST (and
    // re-audit) per parent render, while an in-place edit DOES change the payload key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey, subjectRevisionId, retrievedAt]);

  return {
    finding,
    notes,
    error,
    errorKind,
    loading,
    featureUnavailable,
    revisionContextMissing: Boolean(failureMode) && !subjectRevisionId,
    retrievalTimeMissing: Boolean(failureMode && subjectRevisionId) && !retrievedAt,
  };
};
